import React from "react";
import {autobind} from "core-decorators";
import {action, computed, observable} from "mobx";
import {observer} from "mobx-react";
import {Alert, Col, InputNumber, message, notification, Modal, Row, Statistic, Typography} from "antd";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import pluralize from "pluralize";
import _isFinite from "lodash/isFinite";
import _isNumber from "lodash/isNumber";

import {scoreCreditsPrice} from "@/shared/plans";
import {buyCredits} from "@/lib/backend";
import {priceToString} from "../prices";
import StoreContext from "@/stores/StoreContext";

export interface IScoreCreditsModalProps {
  credits: number;
  close: () => void;
  open: boolean;
}

@autobind
@observer
export default class ScoreCreditsModal extends React.Component<IScoreCreditsModalProps> {
  static contextType = StoreContext;
  context!: React.ContextType<typeof StoreContext>;
  @observable creditsToBuy: number = 1;

  currentDate = new Date();
  discountEndDate = new Date("2024-01-15 23:59:59");

  async buyCredits() {
    try {
      await buyCredits({credits: this.creditsToBuy});
      // await session.fetchApolloData(session.voicefulUser!.id);
      message.success(
        `Thanks! The purchase of ${pluralize("score credit", this.creditsToBuy, true)} has been successful.`,
        7,
      );
      this.props.close();
    } catch (error) {
      const errorMessage = error?.toJSON?.()?.error;
      const description = errorMessage ?? "Unknown error";
      notification.error({message: "Error", description, duration: 0, placement: "bottomRight"});
    }
  }

  @computed({keepAlive: true})
  get price() {
    return scoreCreditsPrice(this.creditsToBuy);
  }

  openConfirmation() {
    Modal.confirm({
      title: `You are going to buy ${pluralize("score credits", this.creditsToBuy, true)} for ${priceToString(
        this.price,
      )}€. Do you want to proceed?`,
      icon: <ExclamationCircleOutlined />,
      content: "If you click OK you will be charged immediately",
      onOk: this.buyCredits,
    });
  }

  reset() {
    this.setCreditsToBuy(1);
  }

  @action
  setCreditsToBuy(credits: any) {
    if (_isNumber(credits) && _isFinite(credits)) this.creditsToBuy = credits;
  }

  public render() {
    const {credits, close, open} = this.props;

    return (
      <Modal
        className="ScoreCreditsModal"
        title="Score credits"
        open={open}
        okText="Buy"
        onCancel={close}
        onOk={this.openConfirmation}
        afterClose={this.reset}
        destroyOnClose={true}
        forceRender={true}
      >
        <Row justify="start" align="middle">
          <Col flex={1}>
            You currently have{" "}
            <strong>
              {credits} score credit{credits === 1 ? "" : "s"}
            </strong>{" "}
            available. <br />
            The price of 1 score credit is 1 euro. <br />
            Please select how many score credits you want to buy:
          </Col>
        </Row>
        <Row justify="center" gutter={32} style={{marginTop: 10}}>
          <Col>
            <Typography.Title level={5}>Score credits</Typography.Title>
            <InputNumber min={1} value={this.creditsToBuy} onChange={this.setCreditsToBuy} size="large" />
          </Col>
          <Col>
            <Typography.Title level={5}>Total price</Typography.Title>
            <Statistic value={priceToString(this.price)} suffix="€" />
          </Col>
        </Row>
        {this.currentDate <= this.discountEndDate && (
          <Row style={{marginTop: 20}}>
            <Col flex={1} style={{textAlign: "center"}}>
              <Alert
                type="info"
                message={
                  <span>
                    🎄 Holiday season special offer!!
                    <br />
                    Get a <strong>50% discount</strong> before January 15, 2024
                  </span>
                }
              />
            </Col>
          </Row>
        )}
      </Modal>
    );
  }
}
